import {
  getLocalStorageItem,
  getSessionStorageItem,
} from '@square/onetrust-compliant-access';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import {
  ApiExplorerContextProvider,
  useApiExplorerContext,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { useSearchParamsObject } from '@squareup/dex-utils-shared-routing-hooks';
import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import { accessTokenReducer } from './reducers/access-token-reducer';

interface Props {}

const ApiExplorerAccessTokenContainer: FC<PropsWithChildren<Props>> = ({
  children,
}) => {
  const searchParams = useSearchParamsObject();
  const contextValue = useApiExplorerContext();

  const envParam = searchParams['env'];
  const parsedEnv = useMemo(
    () =>
      envParam && envParam === 'production'
        ? ApplicationType.Production
        : envParam === 'sandbox'
        ? ApplicationType.Sandbox
        : undefined,
    [envParam]
  );
  const defaultEnvironment = useMemo(
    () =>
      parsedEnv ||
      (getLocalStorageItem('api-explorer-environment') as ApplicationType) ||
      ApplicationType.Sandbox,
    [parsedEnv]
  );

  const [accessTokenData, dispatchAccessTokenAction] = useReducer(
    accessTokenReducer,
    {
      productionClient: '',
      environment: defaultEnvironment,
      token: '',
    }
  );

  useEffect(() => {
    // Get the stored access token for the environment and set it in the context.
    // Dispatching in a useEffect to avoid hydration mismatching on initial render.
    const storedAccessToken = getSessionStorageItem(
      `api-explorer-access-token:${defaultEnvironment.toLowerCase()}`
    ) as string;
    if (storedAccessToken) {
      dispatchAccessTokenAction({
        type: 'SET_TOKEN',
        payload: storedAccessToken,
      });
    }
  }, [defaultEnvironment]);

  const memoizedContext = useMemo(() => {
    return {
      ...contextValue,
      accessTokenData,
      dispatchAccessTokenAction,
    };
  }, [accessTokenData, contextValue, dispatchAccessTokenAction]);

  return (
    <ApiExplorerContextProvider value={memoizedContext}>
      {children}
    </ApiExplorerContextProvider>
  );
};

export { ApiExplorerAccessTokenContainer };
